/**
 * quick-buy.js — Quick-buy Web Component for product cards.
 *
 * Wraps the ATC button on each product card. On click:
 *   - Single-variant products: adds to cart immediately.
 *   - Products with sizes, ≥ 640px: shows a size-selector popup filtered to
 *     the card's current colour. Selecting a size adds to cart and opens
 *     the cart drawer via TRYEvents.
 *   - Products with sizes, < 640px: opens a bottom-sheet drawer (quantity
 *     stepper + size selector) instead of the popup. Jeans (product.type
 *     "jeans") get two size rows — the single Shopify size option (e.g.
 *     "29/30") is split into waist/length for display and recombined to
 *     find the matching variant.
 *
 * Depends on: component.js (TRYComponent, FocusTrap, swipeToDismiss), events.js (TRYEvents)
 */

const QB_COLOR_NAMES = ['color', 'colour', 'farge', 'farve'];
const QB_SIZE_NAMES  = ['size', 'størrelse', 'storlek'];
const QB_MOBILE_QUERY = window.matchMedia('(max-width: 639px)');

class QuickBuy extends TRYComponent {
  /** The drawer is a shared modal — only one may be open at a time. */
  static _openDrawerInstance = null;

  connectedCallback() {
    super.connectedCallback();
    if (this._initialized) return; // guard against re-entry when element is moved in DOM
    this._initialized = true;

    this._variants  = null;   // lazy-parsed from embedded JSON
    this._popup     = null;   // lazy-created DOM node
    this._drawer    = null;   // lazy-created DOM node (mobile)
    this._adding    = false;  // in-flight cart request guard
    // Whether to render the two-row jeans size UI: product.type is "jeans" AND the
    // size value actually looks like "29/30" — some jeans use normal sizes (S/M/L,
    // 38/40/42) and get the regular single-row treatment. Pre-computed server-side
    // for the embedded-JSON path; recomputed in _fetchAndInit for Voyado cards.
    this._jeansDualSize = this.dataset.jeansDualSize === 'true';

    this._hasSizes  = this.dataset.hasSizes  === 'true';
    this._hasColors = this.dataset.hasColors === 'true';
    this._colorOption = parseInt(this.dataset.colorOption, 10);
    this._sizeOption  = parseInt(this.dataset.sizeOption,  10);
    this._firstColor  = this.dataset.firstColor;

    this._t = {
      title:       this.dataset.tTitle       || 'Legg til i handlekurv',
      addButton:   this.dataset.tAddButton   || 'Legg i handlekurven',
      sizeLabel:   this.dataset.tSizeLabel   || 'Velg størrelse',
      lengthLabel: this.dataset.tLengthLabel || 'Velg lengde',
      waistLabel:  this.dataset.tWaistLabel  || 'Bredde',
      decreaseQty: this.dataset.tDecreaseQty || 'Reduser antall',
      increaseQty: this.dataset.tIncreaseQty || 'Øk antall',
    };

    // Drawer selection state
    this._qty            = 1;
    this._selectedSize    = null; // non-jeans
    this._selectedWaist   = null; // jeans
    this._selectedLength  = null; // jeans
    this._drawerVariantId = null;

    this._boundAtcClick = this._onAtcClick.bind(this);
    this._boundDocClick = this._onDocClick.bind(this);
    this._boundKeyDown  = this._onKeyDown.bind(this);

    this.getRequired('.product-card__atc').addEventListener('click', this._boundAtcClick);
    document.addEventListener('click', this._boundDocClick, true);
    document.addEventListener('keydown', this._boundKeyDown, true);
  }

  disconnectedCallback() {
    const atcBtn = this.querySelector('.product-card__atc');
    if (atcBtn) atcBtn.removeEventListener('click', this._boundAtcClick);
    document.removeEventListener('click', this._boundDocClick, true);
    document.removeEventListener('keydown', this._boundKeyDown, true);
    this._popup?.remove();
    this._popup = null;
    if (QuickBuy._openDrawerInstance === this) QuickBuy._openDrawerInstance = null;
    this._drawerAbort?.abort();
    this._drawerTrap?.release({ restoreFocus: false });
    this._drawer?.remove();
    this._drawer = null;
  }

  // ── Event handlers ───────────────────────────────────────────────

  _onAtcClick(e) {
    e.preventDefault();
    const btn = e.currentTarget;

    // Voyado card: no embedded JSON yet — fetch product data on first click
    if (this._variants === null && this.dataset.productUrl) {
      this._fetchAndInit(btn);
      return;
    }

    if (!this._hasSizes) {
      // Single-variant or no size option — add directly
      this._addToCart(parseInt(btn.dataset.variantId, 10), btn);
      return;
    }

    if (QB_MOBILE_QUERY.matches) {
      this._openDrawer(btn);
      return;
    }

    // Toggle popup
    if (this._popup && !this._popup.hidden) {
      this._closePopup();
    } else {
      this._openPopup();
    }
  }

  _onDocClick(e) {
    if (!this._popup || this._popup.hidden) return;
    // Close if click lands outside both the component and the popup
    if (!this.contains(e.target) && !this._popup.contains(e.target)) {
      this._closePopup();
    }
  }

  _onKeyDown(e) {
    if (e.key !== 'Escape') return;

    if (this._drawer?.classList.contains('is-open')) {
      this._closeDrawer();
      return;
    }

    if (!this._popup || this._popup.hidden) return;
    this._closePopup();
    this.querySelector('.product-card__atc')?.focus();
  }

  // ── Popup lifecycle (≥ 640px) ────────────────────────────────────

  _openPopup() {
    if (!this._popup) {
      const popup = this._createPopup();
      if (!popup.children.length) return; // no sizes to show
      this._popup = popup;
      // Append to the media wrapper (a div), never the image anchor — a size
      // button click inside an <a> would navigate to the product instead of adding.
      const card = this.closest('.product-card') ?? this.closest('.cart-recs__card');
      const anchor = card?.querySelector('.product-card__media, .cart-recs__media');
      anchor?.appendChild(this._popup);
    }
    this._popup.hidden = false;
    this.querySelector('.product-card__atc')?.setAttribute('aria-expanded', 'true');
  }

  _closePopup() {
    if (this._popup) this._popup.hidden = true;
    this.querySelector('.product-card__atc')?.setAttribute('aria-expanded', 'false');
  }

  // ── Popup construction ───────────────────────────────────────────

  _getVariants() {
    if (!this._variants) {
      try {
        const el = this.querySelector('script[type="application/json"]');
        this._variants = el ? JSON.parse(el.textContent) : [];
      } catch {
        this._variants = [];
      }
    }
    return this._variants;
  }

  _isJeans() {
    return this._jeansDualSize === true;
  }

  /**
   * Returns a Map of sizeLabel -> { variantId, available, lowStock }
   * filtered to the card's current colour (if the product has colours).
   * Prefers an available variant when multiple variants share a size label.
   */
  _buildSizeMap() {
    const variants = this._getVariants();
    const map = new Map();

    for (const v of variants) {
      // Filter by colour when the product has a colour option
      if (this._hasColors && v.options[this._colorOption] !== this._firstColor) continue;

      const sizeLabel = v.options[this._sizeOption];
      const entry = map.get(sizeLabel);

      // Keep first entry; upgrade to available variant if we find one
      if (!entry || (v.available && !entry.available)) {
        map.set(sizeLabel, {
          variantId: v.id,
          available: v.available,
          lowStock: v.available && typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 1,
        });
      }
    }

    return map;
  }

  /**
   * Jeans: the single size option holds a combined "waist/length" string
   * (e.g. "29/30") — there is no separate length variant option. Splits it
   * for display and keeps a combo lookup for the exact variant.
   *
   * The length row always lists every length value that exists for any
   * waist (styled unavailable rather than removed) so switching waist never
   * makes a chip disappear.
   *
   * @param {string|null} selectedWaist — availability/low-stock for the
   *   length row is computed against this waist's combos specifically; with
   *   no waist selected, every length shows unavailable until one is picked.
   */
  _buildJeansMaps(selectedWaist) {
    const variants = this._getVariants();
    const waistMap  = new Map();
    const lengthMap = new Map();
    const comboMap  = new Map();

    for (const v of variants) {
      if (this._hasColors && v.options[this._colorOption] !== this._firstColor) continue;

      const sizeValue = v.options[this._sizeOption];
      const parts = String(sizeValue ?? '').split('/');
      if (parts.length !== 2) continue; // malformed — skip rather than guess
      const [waist, length] = parts;

      const lowStock = v.available && typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 1;

      comboMap.set(`${waist}/${length}`, { variantId: v.id, available: v.available, lowStock });

      const wEntry = waistMap.get(waist);
      if (!wEntry || (v.available && !wEntry.available)) {
        waistMap.set(waist, { available: v.available, lowStock });
      }

      if (!lengthMap.has(length)) lengthMap.set(length, { available: false, lowStock: false });
    }

    if (selectedWaist != null) {
      for (const [length, entry] of lengthMap) {
        const combo = comboMap.get(`${selectedWaist}/${length}`);
        entry.available = !!combo?.available;
        entry.lowStock  = !!combo?.lowStock;
      }
    }

    return { waistMap, lengthMap, comboMap };
  }

  _createPopup() {
    const popup = document.createElement('div');
    popup.className = 'quick-buy__popup';
    popup.setAttribute('role', 'group');
    popup.setAttribute('aria-label', this._t.sizeLabel);

    for (const [label, { variantId, available, lowStock }] of this._buildSizeMap()) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'quick-buy__size-btn';
      btn.textContent = label;
      btn.dataset.variantId = variantId;

      if (!available) {
        btn.classList.add('quick-buy__size-btn--unavailable');
        btn.setAttribute('aria-disabled', 'true');
      }
      if (lowStock) {
        btn.classList.add('quick-buy__size-btn--low-stock');
      }

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (btn.getAttribute('aria-disabled') === 'true') return;
        this._addToCart(parseInt(btn.dataset.variantId, 10), btn);
      });

      popup.appendChild(btn);
    }

    return popup;
  }

  // ── Drawer lifecycle (< 640px) ───────────────────────────────────

  _openDrawer(btn) {
    this._ensureDrawer();

    // Fresh selection state each time the drawer opens
    this._qty = 1;
    this._selectedSize = null;
    this._selectedWaist = null;
    this._selectedLength = null;

    // Preselect the card's default variant (mirrors the PDP, which always
    // starts with a full selection) so the CTA is usable immediately instead
    // of requiring waist to be picked before length can resolve to anything.
    const defaultVariant = this._getVariants().find(v => String(v.id) === String(btn.dataset.variantId));
    if (defaultVariant) {
      if (this._isJeans()) {
        const [waist, length] = String(defaultVariant.options[this._sizeOption] ?? '').split('/');
        this._selectedWaist  = waist  ?? null;
        this._selectedLength = length ?? null;
      } else {
        this._selectedSize = defaultVariant.options[this._sizeOption];
      }
    }

    this._populateDrawerProduct(btn);
    this._renderSizeGroups();
    this._updateQtyDisplay();

    if (QuickBuy._openDrawerInstance && QuickBuy._openDrawerInstance !== this) {
      QuickBuy._openDrawerInstance._closeDrawer();
    }
    QuickBuy._openDrawerInstance = this;

    this._drawer.classList.add('is-open');
    this._drawer.removeAttribute('aria-hidden');
    document.body.setAttribute('data-quick-add-open', '');

    this._drawerTrap = new FocusTrap(this._drawer.querySelector('.quick-buy__drawer-panel'));
    this._drawerTrap.activate();
  }

  _closeDrawer() {
    if (!this._drawer) return;
    this._drawer.classList.remove('is-open');
    this._drawer.setAttribute('aria-hidden', 'true');
    document.body.removeAttribute('data-quick-add-open');
    this._drawerTrap?.release();
    this._drawerTrap = null;
    if (QuickBuy._openDrawerInstance === this) QuickBuy._openDrawerInstance = null;
  }

  _ensureDrawer() {
    if (this._drawer) return;

    const drawer = document.createElement('div');
    drawer.className = 'quick-buy__drawer';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.innerHTML = `
      <div class="quick-buy__drawer-overlay" data-drawer-close></div>
      <div class="quick-buy__drawer-panel" role="dialog" aria-modal="true" aria-label="${this._t.title}">
        <div class="quick-buy__drawer-indicator" aria-hidden="true"></div>
        <div class="quick-buy__drawer-header">
          <p class="quick-buy__drawer-title">${this._t.title}</p>
        </div>
        <div class="quick-buy__drawer-body">
          <div class="quick-buy__drawer-product">
            <div class="quick-buy__drawer-image" data-drawer-image></div>
            <div class="quick-buy__drawer-info">
              <div class="quick-buy__drawer-meta" data-drawer-meta></div>
              <div class="quick-buy__drawer-qty">
                <button type="button" class="quick-buy__drawer-qty-btn" data-qty-dec aria-label="${this._t.decreaseQty}">${this._icon('minus', 14)}</button>
                <span class="quick-buy__drawer-qty-value" data-qty-value>1</span>
                <button type="button" class="quick-buy__drawer-qty-btn" data-qty-inc aria-label="${this._t.increaseQty}">${this._icon('plus', 14)}</button>
              </div>
            </div>
          </div>
          <div class="quick-buy__drawer-sizes" data-size-groups></div>
        </div>
        <div class="quick-buy__drawer-footer">
          <button type="button" class="btn btn--secondary btn--lg quick-buy__drawer-cta" data-drawer-cta disabled></button>
        </div>
      </div>
    `;

    document.body.appendChild(drawer);
    this._drawer = drawer;

    const panel      = drawer.querySelector('.quick-buy__drawer-panel');
    const sizeGroups = drawer.querySelector('[data-size-groups]');
    const cta        = drawer.querySelector('[data-drawer-cta]');

    drawer.querySelector('[data-drawer-close]').addEventListener('click', () => this._closeDrawer());
    drawer.querySelector('[data-qty-dec]').addEventListener('click', () => this._changeQty(-1));
    drawer.querySelector('[data-qty-inc]').addEventListener('click', () => this._changeQty(1));
    sizeGroups.addEventListener('click', (e) => this._onDrawerSizeClick(e));

    cta.addEventListener('click', () => {
      if (cta.disabled || this._drawerVariantId == null) return;
      this._addToCart(this._drawerVariantId, cta, this._qty);
    });

    this._drawerAbort = new AbortController();
    swipeToDismiss({
      panel,
      isHandle: (t) => t.closest('.quick-buy__drawer-indicator, .quick-buy__drawer-header'),
      onDismiss: () => this._closeDrawer(),
      signal: this._drawerAbort.signal,
    });
  }

  _icon(name, size) {
    return `<svg class="icon icon--${name}" width="${size}" height="${size}" aria-hidden="true" focusable="false"><use href="#icon-${name}"></use></svg>`;
  }

  /** Clones image/vendor/title/price straight from the surrounding card — avoids
   *  duplicating that data into JSON; works across grid, search and recs card markup. */
  _populateDrawerProduct(btn) {
    const card = this.closest('.product-card') ?? this.closest('.cart-recs__card');
    const imageWrap = this._drawer.querySelector('[data-drawer-image]');
    const metaWrap  = this._drawer.querySelector('[data-drawer-meta]');
    imageWrap.replaceChildren();
    metaWrap.replaceChildren();

    const srcImg = card?.querySelector('.product-card__img, .cart-recs__img');
    if (srcImg) {
      const img = document.createElement('img');
      img.src = srcImg.currentSrc || srcImg.src;
      img.alt = srcImg.alt;
      img.loading = 'lazy';
      imageWrap.appendChild(img);
    }

    const vendor = card?.querySelector('.product-card__vendor, .cart-recs__vendor');
    if (vendor) metaWrap.appendChild(vendor.cloneNode(true));

    const titleText = card?.querySelector('.product-card__title, .cart-recs__name')?.textContent?.trim();
    if (titleText) {
      const name = document.createElement('p');
      name.className = 'quick-buy__drawer-name';
      name.textContent = titleText;
      metaWrap.appendChild(name);
    }

    const prices = card?.querySelector('.product-card__prices, .cart-recs__price');
    if (prices) metaWrap.appendChild(prices.cloneNode(true));
  }

  _changeQty(delta) {
    this._qty = Math.max(1, this._qty + delta);
    this._updateQtyDisplay();
  }

  _updateQtyDisplay() {
    const el = this._drawer?.querySelector('[data-qty-value]');
    if (el) el.textContent = String(this._qty);
  }

  _sortedKeys(map) {
    return [...map.keys()].sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  _buildSizeChip(label, variantId, available, lowStock, isActive) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'quick-buy__size-btn quick-buy__drawer-size-btn';
    btn.setAttribute('role', 'radio');
    btn.textContent = label;
    btn.dataset.value = label;
    if (variantId != null) btn.dataset.variantId = variantId;

    if (!available) {
      btn.classList.add('quick-buy__size-btn--unavailable');
      btn.setAttribute('aria-disabled', 'true');
    }
    if (lowStock) btn.classList.add('quick-buy__size-btn--low-stock');

    if (isActive) {
      btn.classList.add('quick-buy__size-btn--active');
      btn.setAttribute('aria-checked', 'true');
    } else {
      btn.setAttribute('aria-checked', 'false');
    }

    return btn;
  }

  _renderSizeGroups() {
    const container = this._drawer.querySelector('[data-size-groups]');
    container.replaceChildren();

    if (this._isJeans()) this._renderJeansSizeGroups(container);
    else this._renderNormalSizeGroup(container);

    this._updateCta();
  }

  _renderNormalSizeGroup(container) {
    const map = this._buildSizeMap();

    const group = document.createElement('div');
    group.className = 'quick-buy__drawer-size-group';

    const label = document.createElement('p');
    label.className = 'quick-buy__drawer-size-group-label';
    label.textContent = this._t.sizeLabel;
    group.appendChild(label);

    const row = document.createElement('div');
    row.className = 'quick-buy__drawer-size-row';
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', this._t.sizeLabel);

    for (const [value, { variantId, available, lowStock }] of map) {
      row.appendChild(this._buildSizeChip(value, variantId, available, lowStock, value === this._selectedSize));
    }

    group.appendChild(row);
    container.appendChild(group);
  }

  _renderJeansSizeGroups(container) {
    const { waistMap, lengthMap } = this._buildJeansMaps(this._selectedWaist);

    const waistGroup = document.createElement('div');
    waistGroup.className = 'quick-buy__drawer-size-group';
    const waistLabel = document.createElement('p');
    waistLabel.className = 'quick-buy__drawer-size-group-label';
    waistLabel.textContent = this._t.waistLabel;
    waistGroup.appendChild(waistLabel);

    const waistRow = document.createElement('div');
    waistRow.className = 'quick-buy__drawer-size-row';
    waistRow.dataset.sizeDimension = 'waist';
    waistRow.setAttribute('role', 'radiogroup');
    waistRow.setAttribute('aria-label', this._t.waistLabel);
    for (const waist of this._sortedKeys(waistMap)) {
      const { available, lowStock } = waistMap.get(waist);
      waistRow.appendChild(this._buildSizeChip(waist, null, available, lowStock, waist === this._selectedWaist));
    }
    waistGroup.appendChild(waistRow);

    const lengthGroup = document.createElement('div');
    lengthGroup.className = 'quick-buy__drawer-size-group';
    const lengthLabel = document.createElement('p');
    lengthLabel.className = 'quick-buy__drawer-size-group-label';
    lengthLabel.textContent = this._t.lengthLabel;
    lengthGroup.appendChild(lengthLabel);

    const lengthRow = document.createElement('div');
    lengthRow.className = 'quick-buy__drawer-size-row';
    lengthRow.dataset.sizeDimension = 'length';
    lengthRow.setAttribute('role', 'radiogroup');
    lengthRow.setAttribute('aria-label', this._t.lengthLabel);
    for (const length of this._sortedKeys(lengthMap)) {
      const { available, lowStock } = lengthMap.get(length);
      lengthRow.appendChild(this._buildSizeChip(length, null, available, lowStock, length === this._selectedLength));
    }
    lengthGroup.appendChild(lengthRow);

    container.append(waistGroup, lengthGroup);
  }

  _onDrawerSizeClick(e) {
    const chip = e.target.closest('.quick-buy__size-btn');
    if (!chip || chip.getAttribute('aria-disabled') === 'true') return;

    const dimension = chip.closest('.quick-buy__drawer-size-row')?.dataset.sizeDimension;

    if (dimension === 'waist') {
      this._selectedWaist = chip.dataset.value;
      const { lengthMap } = this._buildJeansMaps(this._selectedWaist);
      if (this._selectedLength && !lengthMap.has(this._selectedLength)) this._selectedLength = null;
    } else if (dimension === 'length') {
      this._selectedLength = chip.dataset.value;
    } else {
      this._selectedSize = chip.dataset.value;
    }

    this._renderSizeGroups();
  }

  _updateCta() {
    const cta = this._drawer.querySelector('[data-drawer-cta]');
    let variantId = null;
    let available = false;

    if (this._isJeans()) {
      if (this._selectedWaist && this._selectedLength) {
        const { comboMap } = this._buildJeansMaps(this._selectedWaist);
        const combo = comboMap.get(`${this._selectedWaist}/${this._selectedLength}`);
        if (combo) { variantId = combo.variantId; available = combo.available; }
      }
    } else if (this._selectedSize) {
      const entry = this._buildSizeMap().get(this._selectedSize);
      if (entry) { variantId = entry.variantId; available = entry.available; }
    }

    this._drawerVariantId = variantId;
    const ready = variantId != null && available;
    cta.disabled = !ready;
    cta.textContent = ready ? this._t.addButton : this._t.sizeLabel;
  }

  // ── Lazy-fetch init (Voyado cards) ───────────────────────────────────

  async _fetchAndInit(btn) {
    if (this._adding) return;
    this._adding = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const handle = new URL(this.dataset.productUrl, location.origin)
        .pathname.split('/products/')[1]?.split('/')[0];
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error(`Product fetch ${res.status}`);
      const product = await res.json();

      this._variants = product.variants;
      const productTypeLc = (product.type || '').toLowerCase();
      const opts = (product.options ?? []).map(o => String(o).toLowerCase());
      const colorIdx = opts.findIndex(o => QB_COLOR_NAMES.includes(o));
      const sizeIdx  = opts.findIndex(o => QB_SIZE_NAMES.includes(o));

      this._hasColors   = colorIdx !== -1;
      this._colorOption = colorIdx !== -1 ? colorIdx : 0;
      this._sizeOption  = sizeIdx  !== -1 ? sizeIdx  : 0;

      // If no recognized size option but product has multiple variants, show the popup
      // using the first non-color option so the user can pick rather than getting a random variant.
      if (sizeIdx === -1 && product.variants.length > 1) {
        const fallback = opts.findIndex((_, i) => i !== colorIdx);
        this._sizeOption = fallback !== -1 ? fallback : 0;
        this._hasSizes = true;
      } else {
        this._hasSizes = sizeIdx !== -1;
      }

      const first = product.variants.find(v => v.available) ?? product.variants[0];
      if (!first) throw new Error('No variants found');
      this._firstColor = this._hasColors ? (first[`option${this._colorOption + 1}`] ?? '') : '';
      btn.dataset.variantId = String(first.id);

      const sampleSizeValue = this._hasSizes ? (first[`option${this._sizeOption + 1}`] ?? '') : '';
      this._jeansDualSize = productTypeLc === 'jeans' && String(sampleSizeValue).includes('/');

      this._adding = false;
      btn.removeAttribute('aria-busy');

      if (!this._hasSizes) {
        this._addToCart(first.id, btn);
      } else if (QB_MOBILE_QUERY.matches) {
        this._openDrawer(btn);
      } else {
        this._openPopup();
      }
    } catch (err) {
      console.error('[QuickBuy] fetch failed:', err);
      this._adding = false;
      btn.removeAttribute('aria-busy');
    }
  }

  // ── Cart add ─────────────────────────────────────────────────────

  async _addToCart(variantId, btn, quantity = 1) {
    if (this._adding) return;
    this._adding = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      });
      if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);

      const cartRes = await fetch('/cart.js');
      const cart    = await cartRes.json();

      this._closePopup();
      this._closeDrawer();
      const card = this.closest('.product-card') ?? this.closest('.cart-recs__card');
      const voyadoTicket = card?.querySelector('[data-voyado-ticket]')?.dataset.voyadoTicket ?? null;
      TRYEvents.emit('cart:item-added', { item_count: cart.item_count, voyadoTicket });
    } catch (_) {
      // On error: restore button state silently.
      // The cart drawer shows its own error state if needed.
    } finally {
      this._adding = false;
      btn.removeAttribute('aria-busy');
    }
  }
}

customElements.define('quick-buy', QuickBuy);
